import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Badge,
  Form,
  Modal,
  Row,
  Col,
  Alert,
  Container,
  Spinner,
} from "react-bootstrap";
import {
  Calendar,
  Clock,
  GeoAlt,
  Person,
  Pencil,
  ArrowLeft,
  Telephone,
  FileText,
  Whatsapp,
  Envelope,
  CheckCircle,
  XCircle,
  ClockHistory,
  CalendarCheck,
  CalendarX,
  Send,
  Edit,
} from "react-bootstrap-icons";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import Loader from "../../components/common/Loader";

const AppointmentDetail = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  // State for appointment data
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [communicationType, setCommunicationType] = useState("whatsapp"); // 'whatsapp' or 'email'

  // Form states
  const [editForm, setEditForm] = useState({
    scheduledDate: "",
    scheduledTime: "",
    counselor: "",
    location: "",
    notes: "",
    duration: 60,
    status: "",
  });
  const [communicationMessage, setCommunicationMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchAppointmentDetails();
  }, [appointmentId]);

  const fetchAppointmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/appointments/admin/${appointmentId}`);
      if (response.data.success || response.data.appointment) {
        const apt = response.data.appointment || response.data.data;
        // attach assessment data if provided by backend
        if (response.data.assessment) {
          apt.assessment = response.data.assessment;
        }
        setAppointment(apt);
        setEditForm({
          scheduledDate: apt.scheduledDate
            ? new Date(apt.scheduledDate).toISOString().split("T")[0]
            : "",
          scheduledTime: apt.scheduledTime || "",
          counselor: apt.counselor || "",
          location: apt.location || "",
          notes: apt.notes || "",
          duration: apt.duration || 60,
          status: apt.status || "pending",
        });
      }
    } catch (error) {
      console.error("Error fetching appointment:", error);
      setError("Failed to load appointment details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    try {
      // Prepare data for update - remove status if it's the same as current
      const updateData = { ...editForm };

      const response = await api.put(
        `/appointments/admin/${appointment._id}`,
        updateData,
      );
      if (response.data.success || response.data.appointment) {
        setAppointment(response.data.appointment || response.data.data);
        setShowEditModal(false);
        setSuccessMessage("Appointment details updated successfully!");
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error("Error updating appointment:", error);
      setError("Failed to update appointment. Please try again.");
    }
  };

  const handleWhatsAppClick = () => {
    const phone =
      appointment?.studentId?.phone ||
      appointment?.studentId?.contact ||
      appointment?.studentId?.phoneNumber;
    if (phone) {
      const digits = phone.replace(/\D/g, "");
      window.open(`https://wa.me/${digits}`, "_blank");
    }
  };

  const handleEmailClick = () => {
    const email = appointment?.studentId?.email;
    if (email) {
      const subject = encodeURIComponent("Regarding your appointment");
      const body = encodeURIComponent(
        `Hello ${appointment?.studentId?.name},\n\nRegarding your appointment on ${formatDateTime(
          appointment?.scheduledDate,
          appointment?.scheduledTime,
        )}\n\n`,
      );
      window.open(`mailto:${email}?subject=${subject}&body=${body}`);
    }
  };

  const formatDateTime = (date, time) => {
    if (!date) return "Not scheduled";
    const formattedDate = new Date(date).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return time ? `${formattedDate} at ${time}` : formattedDate;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "confirmed":
        return (
          <Badge bg="success" className="px-3 py-2 fs-6 rounded-pill">
            <CheckCircle className="me-1" /> Confirmed
          </Badge>
        );
      case "pending":
        return (
          <Badge bg="warning" className="px-3 py-2 fs-6 rounded-pill">
            <ClockHistory className="me-1" /> Pending
          </Badge>
        );
      case "completed":
        return (
          <Badge bg="info" className="px-3 py-2 fs-6 rounded-pill">
            <CalendarCheck className="me-1" /> Completed
          </Badge>
        );
      case "cancelled":
        return (
          <Badge bg="danger" className="px-3 py-2 fs-6 rounded-pill">
            <CalendarX className="me-1" /> Cancelled
          </Badge>
        );
      case "rescheduled":
        return (
          <Badge bg="secondary" className="px-3 py-2 fs-6 rounded-pill">
            <Calendar className="me-1" /> Rescheduled
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="px-3 py-2 fs-6 rounded-pill">
            {status}
          </Badge>
        );
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "zoom":
        return (
          <Badge bg="info" className="px-3 py-2 fs-6 rounded-pill">
            <Telephone className="me-1" /> Phone Call
          </Badge>
        );
      case "office":
        return (
          <Badge bg="primary" className="px-3 py-2 fs-6 rounded-pill">
            <GeoAlt className="me-1" /> Office Consultation
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="px-3 py-2 fs-6 rounded-pill">
            {type}
          </Badge>
        );
    }
  };

  if (loading) return <Loader />;

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-primary" onClick={() => navigate(-1)}>
          <ArrowLeft className="me-2" /> Go Back
        </Button>
      </Container>
    );
  }

  if (!appointment) {
    return (
      <Container className="my-5">
        <Alert variant="warning">Appointment not found.</Alert>
        <Button variant="outline-primary" onClick={() => navigate(-1)}>
          <ArrowLeft className="me-2" /> Go Back
        </Button>
      </Container>
    );
  }

  // Assessment percentage (exact) and display variant
  const assessmentExact =
    appointment?.assessment?.exactPercentage != null
      ? appointment.assessment.exactPercentage
      : appointment?.studentId?.totalScore
        ? Math.round((appointment.studentId.totalScore / 91) * 80)
        : null;

  const assessmentRange = appointment?.assessment?.percentageText || null;

  const getAssessmentVariant = (val) => {
    if (val == null) return "secondary";
    if (val >= 65) return "success";
    if (val >= 50) return "warning";
    return "danger";
  };

  const pageStyle = {
    minHeight: "100vh",
    padding: "20px",
    background: "#f8f9fa",
  };

  const cardStyle = {
    borderRadius: "15px",
    border: "none",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    background: "rgba(255,255,255,0.95)",
  };

  return (
    <div style={pageStyle}>
      <div
        style={{
          maxWidth: "100%",
          margin: "0 auto",
          width: "100%",
          paddingLeft: "10px",
          paddingRight: "10px",
        }}
      >
        {/* Back Button and Header */}
        <div className="mb-4">
          <Button
            variant="outline-primary"
            onClick={() => navigate(-1)}
            className="mb-3"
            style={{ borderRadius: "10px" }}
          >
            <ArrowLeft className="me-2" /> Back to Appointments
          </Button>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1 fw-bold">
                <Calendar className="me-3" size={32} />
                Appointment Details
              </h2>
              <p className="text-muted mb-0">
                Appointment ID: {appointment.appointmentId}
              </p>
            </div>
            <div>
              <Button
                variant="primary"
                onClick={() => setShowEditModal(true)}
                style={{
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                }}
              >
                <Pencil className="me-2" /> Edit Details
              </Button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <Alert
              variant="success"
              onClose={() => setSuccessMessage(null)}
              dismissible
            >
              {successMessage}
            </Alert>
          )}

          {/* Status Bar */}
          <Card style={cardStyle} className="mb-4">
            <Card.Body className="p-4">
              <Row className="align-items-center">
                <Col md={2}>
                  <div>
                    <h6 className="text-muted fw-semibold mb-2">
                      Current Status
                    </h6>
                    {getStatusBadge(appointment.status)}
                  </div>
                </Col>
                <Col md={3}>
                  <div>
                    <h6 className="text-muted fw-semibold mb-2">
                      Appointment Type
                    </h6>
                    {getTypeBadge(appointment.appointmentType)}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>

        {/* Main Content */}
        <Row className="g-4 mb-4">
          {/* Student Information */}
          <Col lg={4}>
            <Card style={cardStyle}>
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4">
                  <Person className="me-2 text-primary" />
                  Student Information
                </h5>

                <div className="mb-4">
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      background:
                        "linear-gradient(135deg, #28a74515 0%, #20c99715 100%)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#28a745",
                      marginBottom: "15px",
                    }}
                  >
                    <Person size={40} />
                  </div>

                  <div>
                    <h6 className="fw-bold mb-1">
                      {appointment.studentId?.name || "Unknown"}
                    </h6>
                    <p className="text-muted mb-3 text-break">
                      {appointment.studentId?.email || "Email not available"}
                    </p>
                  </div>
                </div>

                <hr />

                <div className="mb-3">
                  <small className="text-muted fw-semibold d-block mb-1">
                    <Telephone className="me-1" size={14} />
                    Phone Number
                  </small>
                  <div className="fw-medium">
                    {appointment.studentId?.phone ||
                      appointment.studentId?.contact ||
                      appointment.studentId?.phoneNumber ||
                      "Not available"}
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted fw-semibold d-block mb-1">
                    <Envelope className="me-1" size={14} />
                    Email Address
                  </small>
                  <div className="fw-medium text-break">
                    {appointment.studentId?.email || "Not available"}
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted fw-semibold d-block mb-1">
                    <GeoAlt className="me-1" size={14} />
                    City
                  </small>
                  <div className="fw-medium">
                    {appointment.studentId?.city || "Not available"}
                  </div>
                </div>

                {(appointment.assessment ||
                  appointment.studentId?.totalScore) && (
                  <div className="mb-3">
                    <small className="text-muted fw-semibold d-block mb-1">
                      Assessment Result
                    </small>
                    <div className="fw-medium d-flex align-items-center gap-2">
                      <Badge bg={getAssessmentVariant(assessmentExact)} pill>
                        {assessmentExact != null
                          ? `${assessmentExact}%`
                          : "N/A"}
                      </Badge>
                    </div>
                  </div>
                )}

                {appointment.studentId?.program && (
                  <div className="mb-3">
                    <small className="text-muted fw-semibold d-block mb-1">
                      Study Program
                    </small>
                    <div className="fw-medium">
                      {appointment.studentId.program}
                    </div>
                  </div>
                )}

                {appointment.studentId?.university && (
                  <div>
                    <small className="text-muted fw-semibold d-block mb-1">
                      Target University
                    </small>
                    <div className="fw-medium">
                      {appointment.studentId.university}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Timeline/Meta Info */}
            <Card style={cardStyle} className="mt-4">
              <Card.Body className="p-4">
                <h6 className="fw-bold mb-3">Appointment Timeline</h6>

                <div className="mb-2">
                  <small className="text-muted">Created</small>
                  <div className="small fw-medium">
                    {appointment.createdAt
                      ? new Date(appointment.createdAt).toLocaleString()
                      : "N/A"}
                  </div>
                </div>

                <div>
                  <small className="text-muted">Last Updated</small>
                  <div className="small fw-medium">
                    {appointment.updatedAt
                      ? new Date(appointment.updatedAt).toLocaleString()
                      : "N/A"}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Appointment Information */}
          <Col lg={8}>
            <Card style={cardStyle} className="mb-4">
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4">
                  <Calendar className="me-2 text-primary" />
                  Appointment Information
                </h5>

                <Row className="mb-4">
                  <Col md={6} className="mb-3">
                    <div>
                      <small className="text-muted fw-semibold">
                        <Calendar className="me-1" size={14} />
                        Scheduled Date
                      </small>
                      <div className="fw-bold mt-2">
                        {appointment.scheduledDate
                          ? new Date(
                              appointment.scheduledDate,
                            ).toLocaleDateString("en-US", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })
                          : "Not scheduled"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div>
                      <small className="text-muted fw-semibold">
                        <Clock className="me-1" size={14} />
                        Scheduled Time
                      </small>
                      <div className="fw-bold mt-2">
                        {appointment.scheduledTime || "Not set"}
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={6} className="mb-3">
                    <div>
                      <small className="text-muted fw-semibold">
                        <Person className="me-1" size={14} />
                        Counselor
                      </small>
                      <div className="fw-bold mt-2">
                        {appointment.counselor || "Not assigned"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div>
                      <small className="text-muted fw-semibold">
                        <GeoAlt className="me-1" size={14} />
                        Location
                      </small>
                      <div className="fw-bold mt-2">
                        {appointment.location || "Not specified"}
                      </div>
                    </div>
                  </Col>
                </Row>

                {appointment.notes && (
                  <div>
                    <small className="text-muted fw-semibold">
                      <FileText className="me-1" size={14} />
                      Notes
                    </small>
                    <div
                      className="mt-2 p-3"
                      style={{ background: "#f8f9fa", borderRadius: "8px" }}
                    >
                      <p className="mb-0 text-break">{appointment.notes}</p>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Communication Section */}
            <Card style={cardStyle}>
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4">
                  <Send className="me-2 text-primary" />
                  Communication with Student
                </h5>

                <div className="d-grid gap-2">
                  <Button
                    variant="outline-success"
                    size="lg"
                    className="py-3 d-flex align-items-center justify-content-center fw-semibold"
                    onClick={handleWhatsAppClick}
                    style={{ borderRadius: "10px" }}
                    disabled={
                      !appointment?.studentId?.phone &&
                      !appointment?.studentId?.contact &&
                      !appointment?.studentId?.phoneNumber
                    }
                  >
                    <Whatsapp className="me-2" size={24} />
                    Send WhatsApp Message
                  </Button>

                  <Button
                    variant="outline-secondary"
                    size="lg"
                    className="py-3 d-flex align-items-center justify-content-center fw-semibold"
                    onClick={handleEmailClick}
                    style={{ borderRadius: "10px" }}
                    disabled={!appointment?.studentId?.email}
                  >
                    <Envelope className="me-2" size={24} />
                    Send Email
                  </Button>
                </div>

                {!appointment?.studentId?.phone &&
                  !appointment?.studentId?.contact &&
                  !appointment?.studentId?.phoneNumber && (
                    <Alert variant="warning" className="mt-3 mb-0">
                      <small>
                        <Whatsapp className="me-1" size={14} />
                        Phone number not available for this student
                      </small>
                    </Alert>
                  )}

                {!appointment?.studentId?.email && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    <small>
                      <Envelope className="me-1" size={14} />
                      Email not available for this student
                    </small>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Edit Modal - Now includes Status Update */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            <Pencil className="me-2" />
            Edit Appointment Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            {/* Status Section */}
            <div className="mb-4 p-3 border rounded">
              <h6 className="fw-bold mb-3 d-flex align-items-center">
                <CheckCircle className="me-2 text-primary" />
                Update Status
              </h6>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-medium">
                      Current Status
                    </Form.Label>
                    <div className="mb-2">
                      {getStatusBadge(appointment.status)}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-medium">New Status</Form.Label>
                    <Form.Select
                      value={editForm.status}
                      onChange={(e) =>
                        setEditForm({ ...editForm, status: e.target.value })
                      }
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    >
                      {/* <option value="pending">Pending</option> */}
                      <option value="confirmed">Confirmed</option>
                      {/* <option value="completed">Completed</option> */}
                      <option value="rescheduled">Rescheduled</option>
                      <option value="cancelled">Cancelled</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Appointment Details Section */}
            <h6 className="fw-bold mb-3 d-flex align-items-center">
              <Calendar className="me-2 text-primary" />
              Appointment Details
            </h6>

            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Calendar className="me-1" />
                    Scheduled Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={editForm.scheduledDate}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        scheduledDate: e.target.value,
                      })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Clock className="me-1" />
                    Scheduled Time
                  </Form.Label>
                  <Form.Control
                    type="time"
                    value={editForm.scheduledTime}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        scheduledTime: e.target.value,
                      })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Person className="me-1" />
                    Counselor
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter counselor name"
                    value={editForm.counselor}
                    onChange={(e) =>
                      setEditForm({ ...editForm, counselor: e.target.value })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <GeoAlt className="me-1" />
                    Location
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter location"
                    value={editForm.location}
                    onChange={(e) =>
                      setEditForm({ ...editForm, location: e.target.value })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">
                <FileText className="me-1" />
                Notes
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Add any notes or special instructions"
                value={editForm.notes}
                onChange={(e) =>
                  setEditForm({ ...editForm, notes: e.target.value })
                }
                className="py-2"
                style={{ borderRadius: "8px" }}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="light"
                onClick={() => setShowEditModal(false)}
                className="px-4"
                style={{ borderRadius: "10px" }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleEditSubmit}
                className="px-4"
                style={{
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                }}
              >
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AppointmentDetail;
